import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cartApi } from "../services/api/cartApi";
import { paymentsApi } from "../services/api/paymentsApi";
import { ordersApi } from "../services/api/orderApi";
import { useAuth } from "../contexts/AuthContext";
import { ICart, ICartItem } from "../types";
import { toast } from "react-toastify";
import ScrollToTop from "../components/ScrollToTop";
import ImageWithLoader from "../components/ImageWithLoader";

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [cart, setCart] = useState<ICart | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [notes, setNotes] = useState("");

  // Check if this is a direct buy now checkout (from URL state)
  // const isDirectBuy = location.state?.isDirectBuy || false;
  // const directProductId = location.state?.productId;

  const fetchCart = useCallback(async () => {
    if (!isLoggedIn) {
      toast.error("Please login to proceed with checkout");
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const response = await cartApi.getCart();
      setCart(response.data);
    } catch (error: any) {
      console.error("Error fetching cart:", error);

      if (error?.response?.status === 401) {
        toast.error("Please login to proceed with checkout");
        navigate("/login");
      } else {
        toast.error("Failed to load cart");
      }
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleProceedToPayment = async () => {
    if (!isLoggedIn) {
      toast.error("Please login to proceed with checkout");
      navigate("/login");
      return;
    }

    if (!cart || cart.items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    try {
      setProcessingCheckout(true);

      // Check if user owns any products in cart
      const productIds = cart.items.map(item => item.product._id);
      const ownershipResponse: any = await ordersApi.checkMultipleProductsOwnership(productIds);

      if (ownershipResponse.success && ownershipResponse.data?.ownershipMap) {
        const ownedProducts = Object.entries(ownershipResponse.data.ownershipMap)
          .filter(([_, owned]) => owned)
          .map(([productId]) => {
            const item = cart.items.find(i => i.product._id === productId);
            return item?.product.name || "Unknown Product";
          });

        if (ownedProducts.length > 0) {
          toast.error(
            `You already own: ${ownedProducts.join(", ")}. Please remove them from cart.`
          );
          setProcessingCheckout(false);
          return;
        }
      }

      toast.info("Redirecting to payment...");

      // Create checkout session
      const response = await paymentsApi.createCheckoutSession(notes);

      console.log("Checkout response:", response);

      // Check different possible response structures
      const checkoutUrl = response?.url || response?.data?.url;

      if (checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = checkoutUrl;
      } else {
        console.error("Response structure:", response);
        throw new Error("No checkout URL received");
      }
    } catch (error: any) {
      console.error("Error creating checkout session:", error);

      if (error?.response?.status === 401) {
        toast.error("Please login to proceed with checkout");
        navigate("/login");
      } else {
        toast.error(error?.response?.data?.message || "Failed to create checkout session. Please try again.");
      }
      setProcessingCheckout(false);
    }
  };

  const calculateSubtotal = () => {
    if (!cart) return 0;
    return cart.items.reduce((total, item) => {
      const itemPrice = parseFloat(item.product.price.toString()) || 0;
      return total + itemPrice; // Always quantity 1 for digital products
    }, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal(); // No tax for digital products
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading checkout...</p>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-warning" role="alert">
          <h4>Your cart is empty</h4>
          <p>Add some products to your cart before proceeding to checkout.</p>
          <button 
            className="btn btn-primary"
            onClick={() => navigate("/products?collection=Canvas")}
          >
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex flex-column">
      <ScrollToTop />
      {/* Breadcrumb */}
      <div className="py-3 font_two">
        <div className="container">
          <p className="text-muted small mb-0 font_two">
            <a href="/" className="text-muted text-decoration-none">Home</a> / 
            <a href="/cart" className="text-muted text-decoration-none">Cart</a> / 
            <span className="fw-bold">Checkout</span>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container flex-grow-1 py-4">
        <div className="row">
          {/* Left Column - Order Summary */}
          <div className="col-lg-8 mb-4">
            <div className="bg-white p-4 rounded-3 shadow-sm">
              <h4 className="mb-4">Order Summary</h4>
              
              {/* Cart Items */}
              <div className="mb-4">
                {cart.items.map((item: ICartItem, index: number) => (
                  <div key={`${item.product._id}-${index}`} className="d-flex align-items-center mb-3 p-3 border rounded">
                    <div className="flex-shrink-0 me-3">
                      <ImageWithLoader
                        src={item.product.image || "/assets/images/placeholder.png"}
                        alt={item.product.name}
                        className="rounded"
                        style={{ width: "80px", height: "80px", objectFit: "cover" }}
                        fallbackSrc="/assets/images/placeholder.png"
                      />
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{item.product.name}</h6>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Digital Product</span>
                        <span className="fw-bold">${parseFloat(item.product.price.toString()).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Notes */}
              <div className="mb-4">
                <label htmlFor="orderNotes" className="form-label fw-medium">
                  Order Notes (Optional)
                </label>
                <textarea
                  id="orderNotes"
                  className="form-control"
                  rows={3}
                  placeholder="Any special instructions for your order..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Payment Summary */}
          <div className="col-lg-4">
            <div className="bg-white p-4 rounded-3 shadow-sm">
              <h5 className="mb-4">Payment Summary</h5>
              
              <div className="d-flex justify-content-between mb-2">
                <span>Subtotal ({cart.items.length} digital products)</span>
                <span>${calculateSubtotal().toFixed(2)}</span>
              </div>
              
              <hr />
              
              <div className="d-flex justify-content-between mb-4">
                <strong>Total</strong>
                <strong>${calculateTotal().toFixed(2)}</strong>
              </div>

              {/* Payment Button */}
              <button
                onClick={handleProceedToPayment}
                disabled={processingCheckout}
                className="btn btn-dark w-100 py-3 rounded-pill"
                style={{
                  cursor: processingCheckout ? 'not-allowed' : 'pointer',
                  opacity: processingCheckout ? 0.7 : 1
                }}
              >
                {processingCheckout ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  "Proceed to Payment"
                )}
              </button>

              {/* Security Notice */}
              <div className="mt-4 p-3 bg-light rounded">
                <div className="d-flex align-items-center">
                  <i className="fas fa-shield-alt text-success me-2"></i>
                  <small className="text-muted">
                    Your payment information is secure and encrypted
                  </small>
                </div>
                <div className="d-flex align-items-center mt-2">
                  <i className="fas fa-gift text-primary me-2"></i>
                  <small className="text-muted">
                    Digital products - No tax applied
                  </small>
                </div>
              </div>

              {/* Back to Cart */}
              <div className="mt-3 text-center">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigate("/cart")}
                >
                  Back to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
